import React, { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isBrowser } from '../utils/isServer';
import App from '../App';
import AboutPage from './AboutPage';
import FAQPage from './FAQPage';
import HowToPage from './HowToPage';
import PrivacyPage from './PrivacyPage';
import { UltimateGuide } from './UltimateGuide';
import { ConsumerRightsPage } from './ConsumerRightsPage';
import { ManagingSymptomsPage } from './ManagingSymptomsPage';
import { FoodPoisoningReportPage } from './FoodPoisoningReportPage';
import AnalyticsManager from '../utils/AnalyticsManager';
import LoadingIndicator from './LoadingIndicator';
import { lazyWithRetry } from '../utils/lazyWithRetry';
import AdminApiService from '../utils/AdminApiService';
import { NavigationProvider } from '../utils/SimpleNavigation';

// Admin pages never render during SSR (the server sends the SPA shell for
// /admin) and are irrelevant to visitors — keep them out of the main bundle.
const AdminLogin = lazyWithRetry(() => import('./admin/AdminLogin'));
const AdminDashboard = lazyWithRetry(() => import('./admin/AdminDashboard'));
const RestaurantManagement = lazyWithRetry(() => import('./admin/RestaurantManagement'));
const RestaurantEdit = lazyWithRetry(() => import('./admin/RestaurantEdit'));
const ScrapingManagement = lazyWithRetry(() => import('./admin/ScrapingManagement'));
const NewScrapingJob = lazyWithRetry(() => import('./admin/NewScrapingJob'));
const AdminLogs = lazyWithRetry(() => import('./admin/AdminLogs'));
const ReportsManagement = lazyWithRetry(() => import('./admin/ReportsManagement'));
const BackupManagement = lazyWithRetry(() => import('./admin/BackupManagement'));
const SubscriptionManagement = lazyWithRetry(() => import('./admin/SubscriptionManagement'));

export type PageType = 'home' | 'restaurant' | 'city' | 'province' | 'chain' | 'chains' | 'backlog' | 'about' | 'faq' | 'how-to-check' | 'privacy' | 'ultimate-guide' | 'consumer-rights' | 'managing-symptoms' | 'report-food-poisoning' | 'recent-reports' | 'trending' | 'admin-login' | 'admin-dashboard' | 'admin-restaurants' | 'admin-restaurant-edit' | 'admin-scraping' | 'admin-scraping-new' | 'admin-logs' | 'admin-reports' | 'admin-backup' | 'admin-subscriptions';

export class SimpleRouter {
  private static serverUrl: string | null = null;

  static setServerUrl(url: string): void {
    this.serverUrl = url;
  }

  static getCurrentPath(): string {
    if (this.serverUrl !== null) {
      // On server, parse just the pathname from the full URL
      try {
        const parsed = new URL(this.serverUrl, 'http://localhost');
        return parsed.pathname;
      } catch {
        return this.serverUrl.split('?')[0];
      }
    }
    return window.location.pathname;
  }

  static getCurrentPage(): PageType {
    const path = this.getCurrentPath();

    // Remove language prefix if present
    const pathWithoutLang = path.replace(/^\/[a-z]{2}(\/|$)/, '/');

    // Admin routes
    if (pathWithoutLang.startsWith('/admin')) {
      if (pathWithoutLang === '/admin/login') return 'admin-login';
      if (pathWithoutLang === '/admin' || pathWithoutLang === '/admin/dashboard') return 'admin-dashboard';
      if (pathWithoutLang.match(/^\/admin\/restaurants\/\d+$/)) return 'admin-restaurant-edit';
      if (pathWithoutLang === '/admin/restaurants') return 'admin-restaurants';
      if (pathWithoutLang === '/admin/scraping/new') return 'admin-scraping-new';
      if (pathWithoutLang === '/admin/scraping') return 'admin-scraping';
      if (pathWithoutLang === '/admin/logs') return 'admin-logs';
      if (pathWithoutLang === '/admin/reports') return 'admin-reports';
      if (pathWithoutLang === '/admin/backup') return 'admin-backup';
      if (pathWithoutLang === '/admin/subscriptions') return 'admin-subscriptions';

      // Default to dashboard for unknown admin routes
      return 'admin-dashboard';
    }

    if (pathWithoutLang.startsWith('/restaurant/')) return 'restaurant';
    if (pathWithoutLang === '/' || pathWithoutLang === '') return 'home';
    if (pathWithoutLang.startsWith('/about')) return 'about';
    if (pathWithoutLang.startsWith('/faq')) return 'faq';
    if (pathWithoutLang.startsWith('/how-to-check')) return 'how-to-check';
    if (pathWithoutLang.startsWith('/privacy')) return 'privacy';
    if (pathWithoutLang.startsWith('/report-food-poisoning')) return 'report-food-poisoning';
    if (pathWithoutLang.startsWith('/ultimate-guide')) return 'ultimate-guide';
    if (pathWithoutLang.startsWith('/consumer-rights')) return 'consumer-rights';
    if (pathWithoutLang.startsWith('/managing-symptoms')) return 'managing-symptoms';
    if (pathWithoutLang.startsWith('/recent-reports')) return 'recent-reports';
    if (pathWithoutLang.startsWith('/trending')) return 'trending';
    if (pathWithoutLang.startsWith('/stad/') || pathWithoutLang.startsWith('/ville/') || pathWithoutLang.startsWith('/city/')) return 'city';
    if (pathWithoutLang.startsWith('/provincie/')) return 'province';
    // Rankings before detail: FR plural "chaines" contains singular "chaine"
    if (/^\/(?:ketens|chaines|chains)\/?$/.test(pathWithoutLang)) return 'chains';
    if (/^\/(?:keten|chaine|chain)\/.+/.test(pathWithoutLang)) return 'chain';
    if (/^\/(?:inspectie-achterstand|retard-inspection|inspection-backlog)\/?$/.test(pathWithoutLang)) return 'backlog';

    return 'home'; // Default fallback
  }

  static getChainSlug(): string | null {
    const path = this.getCurrentPath();
    const pathWithoutLang = path.replace(/^\/[a-z]{2}(\/|$)/, '/');
    const match = pathWithoutLang.match(/^\/(?:keten|chaine|chain)\/(.+)$/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  static getCitySlug(): string | null {
    const path = this.getCurrentPath();
    const pathWithoutLang = path.replace(/^\/[a-z]{2}(\/|$)/, '/');
    const match = pathWithoutLang.match(/^\/(?:stad|ville|city)\/(.+)$/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  static getRestoSlug(): string | null {
    const path = this.getCurrentPath();
    const pathWithoutLang = path.replace(/^\/[a-z]{2}(\/|$)/, '/');
    const match = pathWithoutLang.match(/^\/restaurant\/(.+)$/);
    return match ? match[1] : null;
  }

  static getPageTitle(page: PageType): string {
    const titles = {
      'home': 'Restaurant Food Safety Map Belgium',
      'restaurant': 'Restaurant Details - Food Safety Platform',
      'about': 'About - Food Safety Platform',
      'faq': 'FAQ - Food Safety Questions',
      'how-to-check': 'How to Check Restaurant Safety',
      'privacy': 'Privacy Policy - Food Safety Platform',
      'ultimate-guide': 'Ultimate Guide to Reporting Food Poisoning in Belgium',
      'consumer-rights': 'Your Rights as a Consumer - Food Poisoning Belgium',
      'managing-symptoms': 'Managing Food Poisoning Symptoms - What to Do',
      'report-food-poisoning': 'Report Food Poisoning - Food Safety Platform',
      'city': 'City Food Safety - Restaurant Hygiene',
      'province': 'Province Food Safety - Restaurant Hygiene',
      'chain': 'Chain Hygiene Scores - Food Safety Platform',
      'chains': 'Chains Compared by FAVV Inspection Score',
      'backlog': 'Inspection Backlog - Stale Unfavorable Scores',
      'recent-reports': 'Recent Food Safety Reports Belgium',
      'trending': 'Trending Restaurant Hygiene Changes Belgium',
      'admin-login': 'Admin Login - FoodWeb',
      'admin-dashboard': 'Admin Dashboard - FoodWeb',
      'admin-restaurants': 'Restaurant Management - FoodWeb Admin',
      'admin-restaurant-edit': 'Edit Restaurant - FoodWeb Admin',
      'admin-scraping': 'Scraping Jobs - FoodWeb Admin',
      'admin-scraping-new': 'New Scraping Job - FoodWeb Admin',
      'admin-logs': 'System Logs - FoodWeb Admin',
      'admin-reports': 'Reports Management - FoodWeb Admin',
      'admin-backup': 'Backup Management - FoodWeb Admin',
      'admin-subscriptions': 'Subscription Management - FoodWeb Admin'
    };
    return titles[page];
  }

  static navigateTo(path: string): void {
    if (!isBrowser) return;

    // Fixed: Get language from URL or localStorage instead of using hooks
    const currentPath = window.location.pathname;
    const langMatch = currentPath.match(/^\/([a-z]{2})(\/|$)/);
    let currentLang = langMatch ? langMatch[1] : null;

    // Fallback to localStorage or default to 'en'
    if (!currentLang) {
      currentLang = localStorage.getItem('foodweb_language') || 'en';
    }

    // Add language prefix if not present
    const finalPath = path.startsWith(`/${currentLang}`) ? path : `/${currentLang}${path}`;

    window.history.pushState({}, '', finalPath);
    // The popstate handler in Router tracks the page view
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  static updatePageTitle(page: PageType): void {
    if (!isBrowser) return;
    document.title = this.getPageTitle(page);
  }
}

interface NavigationLinkProps {
  to: string;
  children: React.ReactNode;
  className?: string;
  activeClassName?: string;
  onClick?: () => void;
}

export const NavigationLink: React.FC<NavigationLinkProps> = ({
  to,
  children,
  className = '',
  activeClassName = '',
  onClick
}) => {
  const { i18n } = useTranslation();

  // Check if current path matches the link
  const currentPath = isBrowser ? window.location.pathname : SimpleRouter.getCurrentPath();
  const pathWithoutLang = currentPath.replace(/^\/[a-z]{2}(\/|$)/, '/');
  const isActive = pathWithoutLang === to || (to === '/' && pathWithoutLang === '/');

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();

    if (onClick) {
      onClick();
    }

    // Get current language from i18n with fallback
    let currentLang = i18n.language;

    // Fallback if i18n language is not available
    if (!currentLang || currentLang === 'undefined') {
      const currentPath = window.location.pathname;
      const langMatch = currentPath.match(/^\/([a-z]{2})(\/|$)/);
      currentLang = langMatch ? langMatch[1] : localStorage.getItem('foodweb_language') || 'en';
    }

    // Add language prefix to the path
    const finalPath = to.startsWith(`/${currentLang}`) ? to : `/${currentLang}${to}`;

    window.history.pushState({}, '', finalPath);
    // The popstate handler in Router tracks the page view
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const finalClassName = isActive && activeClassName
    ? `${className} ${activeClassName}`
    : className;

  return (
    <a href={to} onClick={handleClick} className={finalClassName}>
      {children}
    </a>
  );
};

export interface RouterProps {
  url?: string;
  initialData?: { restaurant?: any; reports?: any[]; latestReports?: any[]; trends?: { upward?: any[]; downward?: any[] }; cityStats?: any; provinceStats?: any; chainStats?: any; chainRankings?: any; chain?: any; backlog?: any };
}

const Router: React.FC<RouterProps> = ({ url, initialData }) => {
  const { i18n } = useTranslation();

  // On server, set the URL for SimpleRouter
  if (url) {
    SimpleRouter.setServerUrl(url);
  }

  const [currentPage, setCurrentPage] = useState<PageType>(() => SimpleRouter.getCurrentPage());

  useEffect(() => {
    // Manage scroll behavior based on current page
    if (currentPage === 'home' || currentPage === 'restaurant') {
      // Disable scrolling for map view
      document.documentElement.classList.add('no-scroll');
      document.body.classList.add('no-scroll');
    } else {
      // Enable scrolling for all other pages
      document.documentElement.classList.remove('no-scroll');
      document.body.classList.remove('no-scroll');
    }

    // Handle language detection from URL
    const handleLanguageFromURL = () => {
      const path = window.location.pathname;
      const langMatch = path.match(/^\/([a-z]{2})(\/|$)/);

      if (langMatch) {
        const detectedLang = langMatch[1];
        if (detectedLang !== i18n.language && (detectedLang === 'en' || detectedLang === 'nl' || detectedLang === 'fr')) {
          i18n.changeLanguage(detectedLang);
        }
      }
    };

    handleLanguageFromURL();

    const handlePopState = () => {
      handleLanguageFromURL();
      const newPage = SimpleRouter.getCurrentPage();
      setCurrentPage(newPage);
      SimpleRouter.updatePageTitle(newPage);

      // Track page view
      const analytics = AnalyticsManager.getInstance();
      analytics.trackPageView(window.location.pathname, SimpleRouter.getPageTitle(newPage));
    };

    // Listen for navigation events
    window.addEventListener('popstate', handlePopState);

    // Set initial page title
    SimpleRouter.updatePageTitle(currentPage);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      // Cleanup: ensure scrolling is restored
      document.documentElement.classList.remove('no-scroll');
      document.body.classList.remove('no-scroll');
    };
  }, [i18n, currentPage]);

  // Update URL when language changes
  useEffect(() => {
    // Only update URL if language is properly initialized
    if (!i18n.language || i18n.language === 'undefined') {
      return;
    }

    const currentPath = window.location.pathname;
    const pathWithoutLang = currentPath.replace(/^\/[a-z]{2}(\/|$)/, '/');
    const newPath = `/${i18n.language}${pathWithoutLang === '/' ? '' : pathWithoutLang}`;

    if (currentPath !== newPath) {
      window.history.replaceState({}, '', newPath);
    }
  }, [i18n.language]);

  const renderPage = () => {
    // Check authentication for admin routes
    if (isBrowser && currentPage.startsWith('admin-') && currentPage !== 'admin-login') {
      if (!AdminApiService.isAuthenticated()) {
        // Redirect to login
        SimpleRouter.navigateTo('/admin/login');
        return <AdminLogin />;
      }
    }

    switch (currentPage) {
      case 'about':
        return <AboutPage />;
      case 'faq':
        return <FAQPage />;
      case 'how-to-check':
        return <HowToPage />;
      case 'privacy':
        return <PrivacyPage />;
      case 'ultimate-guide':
        return <UltimateGuide />;
      case 'consumer-rights':
        return <ConsumerRightsPage />;
      case 'managing-symptoms':
        return <ManagingSymptomsPage />;
      case 'report-food-poisoning':
        return <FoodPoisoningReportPage />;
      case 'admin-login':
        return <AdminLogin />;
      case 'admin-dashboard':
        return <AdminDashboard />;
      case 'admin-restaurants':
        return <RestaurantManagement />;
      case 'admin-restaurant-edit': {
        // Extract ID from URL for restaurant edit
        const path = isBrowser ? window.location.pathname : SimpleRouter.getCurrentPath();
        const match = path.match(/\/admin\/restaurants\/(\d+)$/);
        const params = match ? { id: match[1] } : undefined;
        return (
          <NavigationProvider params={params}>
            <RestaurantEdit />
          </NavigationProvider>
        );
      }
      case 'admin-scraping':
        return <ScrapingManagement />;
      case 'admin-scraping-new':
        return <NewScrapingJob />;
      case 'admin-logs':
        return <AdminLogs />;
      case 'admin-reports':
        return <ReportsManagement />;
      case 'admin-backup':
        return <BackupManagement />;
      case 'admin-subscriptions':
        return <SubscriptionManagement />;
      case 'city':
      case 'province':
      case 'chain':
      case 'chains':
      case 'backlog':
      case 'recent-reports':
      case 'trending':
      case 'restaurant':
      case 'home':
      default:
        return <App
          initialRestaurant={initialData?.restaurant}
          initialReports={initialData?.reports}
          initialLatestReports={initialData?.latestReports}
          initialTrends={initialData?.trends}
          initialSidebar={
            currentPage === 'recent-reports' ? 'recent-reports' :
            currentPage === 'trending' ? 'trending' :
            currentPage === 'city' ? 'city-stats' :
            currentPage === 'province' ? 'province-stats' :
            currentPage === 'chain' ? 'chain-details' :
            currentPage === 'chains' ? 'chain-rankings' :
            currentPage === 'backlog' ? 'backlog' :
            currentPage === 'restaurant' ? 'restaurant-details' :
            undefined
          }
          citySlug={currentPage === 'city' ? SimpleRouter.getCitySlug() ?? undefined : undefined}
          provinceSlug={currentPage === 'province' ? (() => {
            const path = SimpleRouter.getCurrentPath();
            const match = path.replace(/^\/[a-z]{2}(\/|$)/, '/').match(/^\/provincie\/(.+)$/);
            return match ? decodeURIComponent(match[1]) : undefined;
          })() : undefined}
          chainSlug={currentPage === 'chain' ? SimpleRouter.getChainSlug() ?? undefined : undefined}
          initialCityStats={initialData?.cityStats}
          initialProvinceStats={initialData?.provinceStats}
          initialChainStats={initialData?.chainStats}
          initialChainRankings={initialData?.chainRankings}
          initialChain={initialData?.chain}
          initialBacklog={initialData?.backlog}
          restoSlug={SimpleRouter.getRestoSlug()}
        />;
    }
  };

  // Admin pages are code-split; everything else renders synchronously
  if (currentPage.startsWith('admin-')) {
    return (
      <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><LoadingIndicator isLoading /></div>}>
        {renderPage()}
      </Suspense>
    );
  }

  return renderPage();
};

export default Router;
